"use client";

import { useState } from "react";
import { Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reassurancePoints = ["No pressure, ever", "Complimentary 20-min discovery", "Direct response from Myles"];
const treatmentOptions = ["Brand Strategy", "Creative Direction", "Marketing Consulting", "General Inquiry"];
const visitTimes = [{
  value: "weekday-morning",
  label: "Weekday Morning"
}, {
  value: "weekday-afternoon",
  label: "Weekday Afternoon"
}, {
  value: "flexible",
  label: "Flexible"
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    await handleSubmit();
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="2592" className="p-12 md:p-16 border border-gray-200" style={{
      backgroundColor: "#FAFAF8"
    }}>
        <div className="inline-flex items-center justify-center w-16 h-16 mb-8" style={{
        backgroundColor: "#E8651A"
      }}>
          <Check className="w-8 h-8" style={{
          color: "#FFFFFF"
        }} />
        </div>
        <h3 className="font-default text-3xl md:text-4xl text-gray-900 mb-6 leading-tight">
          Message received. I will be in touch shortly.
        </h3>
        <p className="font-default text-base text-gray-600 mb-10 max-w-md">
          Your inquiry has reached my desk. I personally review every message and will respond within one business day.
        </p>
        <Button onClick={() => {
        resetForm();
        setConsent(false);
      }} variant="outline" className="border-gray-900 bg-transparent text-gray-900 hover:bg-gray-100 h-12 px-6 uppercase tracking-widest text-xs font-bold">
          Send Another
        </Button>
      </div>;
  }
  return <div className="p-8 md:p-12 lg:p-16 border border-gray-200" style={{
    backgroundColor: "#FAFAF8"
  }}>
      <Form onSubmit={onSubmit} className="space-y-8">
        <FormField control={control} name="full_name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-xs uppercase font-bold" style={{
          color: "#E8651A",
          letterSpacing: "0.25em"
        }}>
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} className="h-12 bg-transparent border-0 border-b border-gray-300 px-0 text-gray-900 placeholder:text-gray-400 font-default text-base focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[#E8651A] transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-xs uppercase font-bold" style={{
          color: "#E8651A",
          letterSpacing: "0.25em"
        }}>
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@company.com" {...field} className="h-12 bg-transparent border-0 border-b border-gray-300 px-0 text-gray-900 placeholder:text-gray-400 font-default text-base focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[#E8651A] transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-xs uppercase font-bold" style={{
          color: "#E8651A",
          letterSpacing: "0.25em"
        }}>
                How can I help?
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger className="h-12 bg-transparent border-0 border-b border-gray-300 px-0 text-gray-900 font-default text-base focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[#E8651A] data-[placeholder]:text-gray-400 [&_svg]:text-[#E8651A] [&_svg]:opacity-100">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="border border-gray-200" style={{
            backgroundColor: "#FFFFFF"
          }}>
                  {treatmentOptions.map((opt, i) => <SelectItem key={opt} data-index={i} value={opt} className="font-default text-gray-900 focus:bg-gray-50 focus:text-gray-900">
                      {opt}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-xs uppercase font-bold" style={{
          color: "#E8651A",
          letterSpacing: "0.25em"
        }}>
                Project Context
              </FormLabel>
              <FormControl>
                <Textarea rows={5} placeholder="Tell me about your goals." {...field} className="bg-transparent border-0 border-b border-gray-300 px-0 text-gray-900 placeholder:text-gray-400 font-default text-base focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-[#E8651A] transition-all resize-none min-h-[140px]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="flex items-start gap-4 pt-4">
          <Checkbox id="consent" checked={consent} onCheckedChange={checked => {
          setConsent(checked === true);
          if (checked) setConsentError(false);
        }} className="mt-1 border-gray-300 data-[state=checked]:bg-[#E8651A] data-[state=checked]:border-[#E8651A]" />
          <Label htmlFor="consent" className="font-default text-sm text-gray-600 cursor-pointer leading-relaxed">
            I AGREE TO BE CONTACTED REGARDING MY INQUIRY
          </Label>
        </div>
        {consentError && <p className="text-red-600 text-sm font-default">Please provide your consent to continue.</p>}

        {submitError && <div className="p-4 border border-red-200 bg-red-50">
            <p className="text-red-600 font-default text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-16 font-default text-sm uppercase font-bold tracking-widest hover:opacity-90 transition-opacity mt-4" style={{
        backgroundColor: "#E8651A",
        color: "#FFFFFF"
      }}>
          {isSubmitting ? "Sending..." : "Send Inquiry"}
        </Button>
      </Form>
    </div>;
}
export default function () {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="the-inquiry" className="bg-white text-gray-900 pt-32 md:pt-48 lg:pt-[200px] pb-20 md:pb-[120px] px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 lg:self-start transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-xs uppercase font-bold mb-8" style={{
            color: "#E8651A",
            letterSpacing: "0.25em"
          }}>
              Start a Conversation
            </p>

            <h1 className="font-default font-medium text-gray-900 mb-10" style={{
            fontSize: "clamp(3rem, 8vw, 7.5rem)",
            lineHeight: "0.9",
            letterSpacing: "0.15em"
          }}>
              Let's
              <br />
              Build
              <br />
              <span style={{
              color: "#E8651A"
            }}>Something.</span>
            </h1>

            <p className="font-default text-gray-600 mb-10 leading-relaxed" style={{
            fontSize: "1.25rem",
            maxWidth: "400px"
          }}>
              You built something real. Does the internet know it exists? Let's get found, heard, and trusted.
            </p>

            <ul className="space-y-4 mb-12">
              {reassurancePoints.map((point, i) => <li key={point} data-index={i} className="flex items-center gap-4">
                  <span className="inline-block w-2.5 h-2.5 shrink-0" style={{
                backgroundColor: "#E8651A"
              }} />
                  <span className="font-default text-base text-gray-900">{point}</span>
                </li>)}
            </ul>

            <div className="pt-8 border-t border-gray-200">
              <p className="font-default text-sm text-gray-500 flex items-center gap-3 flex-wrap">
                <Phone className="w-4 h-4" style={{
                color: "#E8651A"
              }} />
                Direct line:{" "}
                <Link to="tel:+15550000000" className="underline underline-offset-4 decoration-1 hover:opacity-80 transition-opacity font-default tracking-wide" style={{
                color: "#E8651A"
              }}>
                  +1 (555) 000-0000
                </Link>
              </p>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="inquiry_form_contact" formId="inquiry_form_contact" sectionName="the-inquiry" defaultValues={{
            full_name: "",
            email: "",
            service_type: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            service_type: {
              required: true
            }
          }} submitText="Send Inquiry" successMessage="Message received. I will be in touch shortly." errorMessage="Something went wrong. Please try again.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}